import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Image, Text } from '@unimed-fesp/components';
import { useLocation } from 'react-router-dom';
import { Form } from '@unform/web';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

// #region Services e Stores
import { Creators as AuthActions } from '../../store/ducks/auth';
import { Creators as UsuarioActions } from '../../store/ducks/usuario';

import {
  USER_KEY,
  getLocalObj,
  excludeData,
  getUserData,
  getRefreshToken,
  isAccessTokenValid,
  getTokenAccessToken,
} from '../../services/auth';
import history from '../../services/history';
// #endregion

// #region Componentes
import SectionHeader from '../../components/atoms/SectionHeader';
import { ErrorToast } from '../../components/molecules/Toast';
import LoadingIcon from '../../components/Icons/LoadingIcon';
import FormChangePassword from '../../components/organisms/FormChangePassword';
// #endregion

import UnimedLogo from '../../assets/images/login/logo-unimed-fesp.svg';
import {
  LoginLink,
  Container,
  ImageStructure,
  HeaderContainer,
  AutenticacaoStructure,
  DificuldadesAcessoContainer,
} from './styles';

function RecuperarSenha() {
  const location = useLocation();
  const isChangePassword =
    false || (location.state && location.state.isChangePassword);

  const { patchSenha } = useSelector((state) => state.usuario);
  const { loggedIn, postRecuperarSenha, usuario } = useSelector(
    (state) => state.auth
  );

  const dispatch = useDispatch();
  const formRef = useRef(null);

  const { REACT_APP_EMAIL_DIFICULDADES_ACESSO } = process.env;
  const mailTo = `mailto:${REACT_APP_EMAIL_DIFICULDADES_ACESSO}`;
  const queryString = window.location.search;
  const params = new URLSearchParams(queryString);
  const queryEmail = params.get('email');
  const [queryToken, setQueryToken] = useState(
    params.get('token')?.replaceAll(' ', '+')
  );

  const changePasswordData = {
    antigaSenha: '',
    novaSenha: '',
    confirmarSenha: '',
  };

  const [changePasswordParams, setChangePasswordParams] =
    useState(changePasswordData);

  const [novaSenha, setNovaSenha] = useState();

  async function handleSubmitNewPasswordToken(data) {
    try {
      const schema = Yup.object().shape({
        novaSenha: Yup.string()
          .trim()
          .matches(
            /^(?=.*?[A-Z])(?=(.*[a-z]){1,})(?=(.*[\d]){1,})(?=(.*[\W_]){1,})(?!.*\s).{6,}$/g,
            'A senha deve ter ao menos 6 caracteres e ser uma combinação de letras maiúsculas e minúsculas, números e simbolos'
          )
          .required('A senha é obrigatória'),
        confirmarSenha: Yup.string()
          .trim()
          .matches(
            /^(?=.*?[A-Z])(?=(.*[a-z]){1,})(?=(.*[\d]){1,})(?=(.*[\W_]){1,})(?!.*\s).{6,}$/g,
            'A senha deve ter ao menos 6 caracteres e deve ser uma combinação de letras maiúsculas e minúsculas, números e simbolos'
          )
          .oneOf([Yup.ref('novaSenha')], 'As senhas devem ser iguais')
          .required('A senha é obrigatória'),
      });
      await schema.validate(data, {
        abortEarly: false,
      });
      dispatch(
        AuthActions.postRecuperarSenhaRequest({
          email: queryEmail,
          senha: data.novaSenha,
          confirmarSenha: data.confirmarSenha,
          token: queryToken,
        })
      );

      setNovaSenha(data.novaSenha);

      formRef.current.setErrors({});
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errorMessages = {};
        error.inner.forEach((err) => {
          errorMessages[err.path] = err.message;
        });

        formRef.current.setErrors(errorMessages);
      }
    }
  }
  async function handleSubmitChangePassword(data) {
    try {
      const schema = Yup.object().shape({
        antigaSenha: Yup.string().required('A senha é obrigatória'),
        novaSenha: Yup.string()
          .trim()
          .matches(
            /^(?=.*?[A-Z])(?=(.*[a-z]){1,})(?=(.*[\d]){1,})(?=(.*[\W_]){1,})(?!.*\s).{6,}$/g,
            'A senha deve ter ao menos 6 caracteres e ser uma combinação de letras maiúsculas e minúsculas, números e simbolos'
          )
          .required('A senha é obrigatória'),
        confirmarSenha: Yup.string()
          .trim()
          .matches(
            /^(?=.*?[A-Z])(?=(.*[a-z]){1,})(?=(.*[\d]){1,})(?=(.*[\W_]){1,})(?!.*\s).{6,}$/g,
            'A senha deve ter ao menos 6 caracteres e deve ser uma combinação de letras maiúsculas e minúsculas, números e simbolos'
          )
          .oneOf([Yup.ref('novaSenha')], 'As senhas devem ser iguais')
          .required('A senha é obrigatória'),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      dispatch(
        UsuarioActions.patchSenhaRequest(
          usuario.idUsuarioLogado,
          data.antigaSenha,
          data.novaSenha,
          data.confirmarSenha
        )
      );

      formRef.current.setErrors({});
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errorMessages = {};

        error.inner.forEach((err) => {
          errorMessages[err.path] = err.message;
        });

        formRef.current.setErrors(errorMessages);
      }
    }
  }

  useEffect(() => {
    if (loggedIn) history.push('/novo/lista');

    if (getLocalObj(USER_KEY) && !loggedIn) {
      if (getTokenAccessToken() && isAccessTokenValid()) {
        dispatch(AuthActions.signInRequest(getUserData()));
      } else if (getRefreshToken() && !isAccessTokenValid()) {
        dispatch(AuthActions.refreshInvalidTokenRequest(getTokenAccessToken()));
      } else {
        excludeData();
        dispatch(AuthActions.signOut());
      }
    }
  }, [dispatch, loggedIn]);

  useEffect(() => {
    if (patchSenha.success) history.push('/novo/lista');
  }, [patchSenha.success]);

  useEffect(() => {
    if (patchSenha.isError) {
      toast.error(<ErrorToast size="30">{patchSenha.error[0]}</ErrorToast>);
    }
  }, [patchSenha.error, patchSenha.isError]);

  useEffect(() => {
    if (postRecuperarSenha.isError) {
      setQueryToken(null);
      history.replace(
        `/novo/login/redefinir-senha?email=${queryEmail}&isTokenError=true`
      );
    } else if (postRecuperarSenha.success) {
      const usuarioParams = {
        userId: queryEmail,
        password: novaSenha,
        grantType: 'password',
      };

      dispatch(AuthActions.postLoginRequest(usuarioParams));
    }
  }, [dispatch, novaSenha, postRecuperarSenha, queryEmail]);

  return (
    <Container>
      <AutenticacaoStructure>
        {isChangePassword ? (
          <HeaderContainer>
            <SectionHeader
              sectionName="movcad"
              subtextSectionName="movimentações cadastrais"
              padding="0px"
            />
            <Image
              marginTop={isChangePassword ? '10px' : false}
              src={UnimedLogo}
              name="Unimed Logo"
              marginLeft="50px"
            />
          </HeaderContainer>
        ) : (
          <>
            <Image
              marginTop={isChangePassword ? '10px' : false}
              src={UnimedLogo}
              name="Unimed Logo"
            />
            <SectionHeader
              sectionName="movcad"
              subtextSectionName="movimentações cadastrais"
            />
          </>
        )}
        <Form
          ref={formRef}
          onSubmit={
            !isChangePassword
              ? handleSubmitNewPasswordToken
              : handleSubmitChangePassword
          }
        >
          <FormChangePassword
            changePasswordParams={changePasswordParams}
            setChangePasswordParams={setChangePasswordParams}
            formRef={formRef.current}
            isForgottenPassword={!isChangePassword}
          />
          <Button
            tipo="primary"
            marginLeft={189.5}
            marginTop={25}
            borderRadius={5}
            border="#00995d"
            height={40}
            width={141}
            type="submit"
            justifyCenter
            alignCenter
            marginBottom={25}
            display={
              postRecuperarSenha.loading || patchSenha.loading ? 'none' : false
            }
            font="16px unimed_slabregular"
          >
            alterar senha
          </Button>
          {(postRecuperarSenha.loading || patchSenha.loading) && (
            <LoadingIcon
              size="35"
              width="400px"
              marginTop="40px"
              marginLeft="60px"
            />
          )}
          {isChangePassword && (
            <DificuldadesAcessoContainer>
              <Text marginRight={8} text="dificuldades no acesso?" />
              <LoginLink
                title={REACT_APP_EMAIL_DIFICULDADES_ACESSO}
                href={mailTo}
              >
                fale com o nosso Suporte.
              </LoginLink>
            </DificuldadesAcessoContainer>
          )}
        </Form>
      </AutenticacaoStructure>
      <ImageStructure />
    </Container>
  );
}

export default RecuperarSenha;
